import { Component, ElementRef, Input, OnInit, AfterContentInit } from "@angular/core";
import _cloneDeep from "lodash/cloneDeep";
import _remove from "lodash/remove";
import { Subscription } from "rxjs";
import { ProductsSearchModeEnum } from "../../../enums/products-search-mode.enum";
import { ProductResultItemPropertyModel } from "../../../models/search/results/items/product-result-item-property.model";
import { ProductResultItemModel } from "../../../models/search/results/items/product-result-item.model";
import { ProductsListViewService } from "../../../services/components/products-list-view.service";

@Component({
    selector: "pcg-ng-products-list-view",
    template: require('./products-list-view.component.html')
})
export class ProductsListViewComponent implements OnInit, AfterContentInit {
    private FEATURE_ALIAS_AGGREGATED_FEATURES: string = "AggregatedFeatures";

    private AGGREGATED_FEATURES_TITLE: string = "Useful Information";
    private AGGREGATED_FEATURES_SEPARATOR: string = ", ";

    private PROPERTY_ALIAS_PRODUCT_NAME: string = "Name";
    private PROPERTY_ALIAS_CATALOGUE_NUMBER: string = "CatalogueNo";
    private PROPERTY_ALIAS_COLLECTION: string = "Collection";

    private isGridViewSubscription: Subscription;

    @Input()
    public mode: ProductsSearchModeEnum;

    @Input()
    public products: Array<ProductResultItemModel> = new Array<ProductResultItemModel>();

    public isGridView: boolean = false;

    public constructor(
        private domElement: ElementRef,
        private productsListViewService: ProductsListViewService
    ) {

    }

    public ngOnInit(): void {
        this.isGridViewSubscription = this.productsListViewService.isGridViewObservable()
            .subscribe(value => this.isGridView = value);

        this.isGridView = this.productsListViewService.isGridView;

        // sm = 576px
        if (window.innerWidth < 576) {
            this.setGridView();
        }
    }

    public ngAfterContentInit(): void {
        this.handleBackButton();
    }

    private handleBackButton(): void {
        const links = this.domElement.nativeElement.getElementsByClassName("product-list-toolbar__back-link");
        for (const link of links) {
            link.href = document.referrer;
            link.onclick = () => history.back();
        }
    }

    public setGridView(): void {
        this.productsListViewService.setGridView();
    }

    public setListView(): void {
        this.productsListViewService.setListView();
    }

    public getHeadings(): Array<ProductResultItemPropertyModel> {
        let features = new Array<ProductResultItemPropertyModel>();
        const product = this.getTargetProduct();
        if (product) {
            features = this.getFeatures(product);
        }

        return features;
    }

    public getFeatures(product: ProductResultItemModel): Array<ProductResultItemPropertyModel> {
        let features = new Array<ProductResultItemPropertyModel>();

        if (product) {
            const properties = _cloneDeep(product.properties).sort((p1, p2) => p1.displayOrder - p2.displayOrder);

            const productName = this.extractFeature(properties, this.PROPERTY_ALIAS_PRODUCT_NAME);
            if (productName) {
                features.push(productName);
            }

            const catalogueNumber = this.extractFeature(properties, this.PROPERTY_ALIAS_CATALOGUE_NUMBER);
            if (catalogueNumber) {
                features.push(catalogueNumber);
            }

            const collection = this.extractFeature(properties, this.PROPERTY_ALIAS_COLLECTION);

            if (this.isGridView) {
                features = features.concat(properties.map(prop => _cloneDeep(prop)));
            } else {
                switch (this.mode) {
                    case ProductsSearchModeEnum.AllCollections:
                    case ProductsSearchModeEnum.CollectionGroup: {
                        const aggregatedFeature: ProductResultItemPropertyModel = {
                            alias: this.FEATURE_ALIAS_AGGREGATED_FEATURES,
                            name: this.AGGREGATED_FEATURES_TITLE,
                            value: properties
                                .map(prop => prop.value)
                                .filter(value => !!value)
                                .join(this.AGGREGATED_FEATURES_SEPARATOR),
                            displayOrder: 0
                        };
                        features.push(aggregatedFeature);
                        break;
                    }
                    case ProductsSearchModeEnum.Collection: {
                        features = features.concat(properties.map(prop => _cloneDeep(prop)));
                        break;
                    }
                }
            }
            if (collection) {
                features.push(collection);
            }
        }

        return features;
    }

    private extractFeature(properties: Array<ProductResultItemPropertyModel>, alias: string): ProductResultItemPropertyModel {
        let feature = null;
        const property = properties.find(prop => prop.alias === alias);
        if (property) {
            feature = _cloneDeep(property);
            _remove(properties, property);
        }

        return feature;
    }

    private getTargetProduct(): ProductResultItemModel {
        if (this.products.length) {
            return this.products[0];
        }

        return null;
    }

    public resolveListStyles(): object {
        const WIDTH_PRODUCT_NAME = "1fr";
        const WIDTH_CATALOGUE_NUMBER = "1fr";
        const WIDTH_AGGREGATED_FEATURE = "3fr";
        const WIDTH_DYNAMIC_FEATURE = "1fr";
        const WIDTH_COLLECTION = "1fr";
        const WIDTH_ADD_TO_WISHLIST = "84px";
        const WIDTH_QUICK_VIEW = WIDTH_ADD_TO_WISHLIST;

        let columnsTemplate = "";

        switch (this.mode) {
            case ProductsSearchModeEnum.AllCollections:
            case ProductsSearchModeEnum.CollectionGroup: {
                columnsTemplate = `${WIDTH_PRODUCT_NAME} ${WIDTH_CATALOGUE_NUMBER} ${WIDTH_AGGREGATED_FEATURE} ${WIDTH_COLLECTION} ${WIDTH_ADD_TO_WISHLIST} ${WIDTH_QUICK_VIEW}`;
                break;
            }
            case ProductsSearchModeEnum.Collection: {
                const headings = this.getHeadings();
                const dynamicFeaturesNumber = headings.length - 1/*product name*/ - 1/*catalogue number*/;
                columnsTemplate = `${WIDTH_PRODUCT_NAME} ${WIDTH_CATALOGUE_NUMBER} ${(dynamicFeaturesNumber > 0 ? `repeat(${dynamicFeaturesNumber},${WIDTH_DYNAMIC_FEATURE})` : "")} ${WIDTH_ADD_TO_WISHLIST} ${WIDTH_QUICK_VIEW}`;
                break;
            }
        }

        return {
            "grid-template-columns": columnsTemplate
        };
    }
}
